import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, Plus, Trash2, ArrowRight } from "lucide-react";

import { adminHeaders, isAdminSession } from "../lib/api";
import { T } from "@/locales/tr";
import { PaymentNotice } from "@/components/PaymentNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PRODUCT = {
  name: "",
  description: "",
  price_eur: "",
  image_url: "",
  supplier_email: "",
  sku: "",
  stock: 100,
};

const EMPTY_BUYER = {
  buyer_name: "",
  buyer_email: "",
  shipping_address: "",
  shipping_city: "",
  shipping_country: "Sénégal",
  shipping_phone: "",
  quantity: 1,
};

export default function Dropship() {
  const [products, setProducts] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [openBuy, setOpenBuy] = useState(null); // product_id en cours d'achat
  const [buyer, setBuyer] = useState(EMPTY_BUYER);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/dropship/products`);
      setProducts(data);
    } catch (e) { console.warn("Dropship products load failed:", e); }
  };
  useEffect(() => { load(); }, []);

  const createProduct = async () => {
    if (!newProduct.name || !newProduct.price_eur || !newProduct.supplier_email) {
      toast.error(T("Nom, prix et email fournisseur requis."));
      return;
    }
    try {
      await axios.post(`${API}/dropship/products`, {
        ...newProduct,
        price_eur: parseFloat(newProduct.price_eur),
        stock: parseInt(newProduct.stock) || 100,
      }, { headers: adminHeaders() });
      toast.success(T("Produit ajouté."));
      setNewProduct(EMPTY_PRODUCT);
      load();
    } catch (e) {
      toast.error(e?.response?.status === 401 ? T("Action réservée à l'administrateur.") : T("Échec."));
    }
  };

  const removeProduct = async (id) => {
    try {
      await axios.delete(`${API}/dropship/products/${id}`, { headers: adminHeaders() });
      toast.success(T("Produit retiré."));
      load();
    } catch { toast.error(T("Action réservée à l'administrateur.")); }
  };

  const buy = async (productId) => {
    if (!buyer.buyer_name || !buyer.buyer_email || !buyer.shipping_address || !buyer.shipping_city || !buyer.shipping_phone) {
      toast.error(T("Tous les champs livraison sont requis."));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/dropship/checkout`, {
        product_id: productId,
        origin_url: window.location.origin,
        ...buyer,
        quantity: parseInt(buyer.quantity) || 1,
      });
      if (data?.url) window.location.href = data.url;
      else {
        toast.error(T("Pas d'URL Stripe."));
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Échec du checkout."));
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-dropship" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">
        {T("Vitrine Dropshipping")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("La boutique de la diaspora.")}<br />
        <span className="italic font-light">{T("Sobre, halal, livrée.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Une sélection serrée de produits utiles, sourcés par Papicoo. Tu paies par carte (")}<strong>{T("Stripe")}</strong>{T("), Papicoo prévient le fournisseur, et la livraison part vers ton adresse au pays.")}
      </p>

      {isAdminSession() && <div className="mt-6 flex flex-wrap gap-2">
        <button
          data-testid="toggle-admin"
          onClick={() => setShowAdmin((s) => !s)}
          className="px-4 py-2 text-sm rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1"
        >
          <Plus size={14} /> {showAdmin ? T("Fermer admin") : T("Ajouter un produit (admin)")}
        </button>
      </div>}

      {showAdmin && isAdminSession() && (
        <div data-testid="admin-form" className="mt-5 papicoo-card p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="admin-name" placeholder={T("Nom du produit")} value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="admin-price" type="number" step="0.01" placeholder={T("Prix EUR")} value={newProduct.price_eur} onChange={(e) => setNewProduct({ ...newProduct, price_eur: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="admin-image" placeholder={T("URL image (https://…)")} value={newProduct.image_url} onChange={(e) => setNewProduct({ ...newProduct, image_url: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="admin-supplier" type="email" placeholder={T("Email fournisseur")} value={newProduct.supplier_email} onChange={(e) => setNewProduct({ ...newProduct, supplier_email: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="admin-sku" placeholder={T("SKU (optionnel)")} value={newProduct.sku} onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="admin-stock" type="number" placeholder={T("Stock")} value={newProduct.stock} onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          </div>
          <textarea data-testid="admin-desc" rows={2} placeholder={T("Description")} value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <button data-testid="admin-submit" onClick={createProduct} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full papicoo-btn-gold text-sm">{T("Ajouter au catalogue")}</button>
        </div>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {products.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 papicoo-card p-6 text-sm italic text-[color:var(--papicoo-muted)]">
            <span data-testid="dropship-coming-soon">{T("Boutique en préparation — aucun produit en vente pour le moment.")}</span>
          </div>
        )}
        {products.map((p) => (
          <div key={p.id} data-testid={`product-${p.id}`} className="papicoo-card overflow-hidden flex flex-col">
            {p.image_url && (
              <img src={p.image_url} alt={T(p.name)} className="w-full h-48 object-cover" loading="lazy" />
            )}
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="font-display text-xl text-[color:var(--papicoo-ink)]">{T(p.name)}</div>
                {isAdminSession() && <button onClick={() => removeProduct(p.id)} className="p-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-accent)]" title={T("Retirer")}>
                  <Trash2 size={12} />
                </button>}
              </div>
              <p className="text-sm text-[color:var(--papicoo-muted)] mt-1.5 flex-1">{T(p.description)}</p>
              <div className="mt-3 flex items-center justify-between">
                <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{p.price_eur} €</div>
                <button
                  data-testid={`buy-${p.id}`}
                  onClick={() => setOpenBuy(openBuy === p.id ? null : p.id)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full papicoo-btn-primary text-sm"
                >
                  <ShoppingBag size={14} /> {T("Acheter")}
                </button>
              </div>

              {openBuy === p.id && (
                <div data-testid={`buy-form-${p.id}`} className="mt-4 space-y-2 border-t border-[color:var(--papicoo-border)] pt-4">
                  <input data-testid="buyer-name" placeholder={T("Ton nom")} value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <input data-testid="buyer-email" type="email" placeholder={T("Ton email")} value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <input data-testid="shipping-address" placeholder={T("Adresse de livraison")} value={buyer.shipping_address} onChange={(e) => setBuyer({ ...buyer, shipping_address: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="shipping-city" placeholder={T("Ville")} value={buyer.shipping_city} onChange={(e) => setBuyer({ ...buyer, shipping_city: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                    <input data-testid="shipping-country" placeholder={T("Pays")} value={buyer.shipping_country} onChange={(e) => setBuyer({ ...buyer, shipping_country: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  </div>
                  <input data-testid="shipping-phone" placeholder={T("Téléphone livraison")} value={buyer.shipping_phone} onChange={(e) => setBuyer({ ...buyer, shipping_phone: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <input data-testid="quantity" type="number" min="1" placeholder={T("Quantité")} value={buyer.quantity} onChange={(e) => setBuyer({ ...buyer, quantity: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <button
                    data-testid={`confirm-buy-${p.id}`}
                    disabled={submitting}
                    onClick={() => buy(p.id)}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full papicoo-btn-primary text-sm disabled:opacity-50"
                  >
                    {submitting ? "…" : <>{T("Payer")} {(p.price_eur * (parseInt(buyer.quantity) || 1)).toFixed(2)} € <ArrowRight size={14} /></>}
                  </button>
                  <PaymentNotice className="pt-1" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
